import React, { createContext, useContext, ReactNode } from 'react';
import { useAuth } from './AuthProvider';
import { useUnseenNotifications } from '../hooks/useUnseenNotifications';

interface IncomingBookingsContextProps {
  /** How many NewBooking notifications have arrived while nobody was actually looking at IncomingBookings -- purely a nav-badge counter. */
  unseenCount: number;
  /** IncomingBookings.tsx calls this on mount/unmount (see its own effect) -- true exactly while that list is on screen, host or embedded-in-MyBookings alike. */
  setViewingIncomingBookings: (viewing: boolean) => void;
}

const IncomingBookingsContext = createContext<IncomingBookingsContextProps | undefined>(undefined);

/** The one notification that means a host has something new to answer. */
const WATCHED_TYPES = ['NewBooking'] as const;

/**
 * Feeds the small coral badge on MobileBottomNav's "Reservas" tab and on
 * HostToolsMenu's "Reservas" row and trigger icon (see both) -- independent of
 * NotificationBell's own unread count, since that one covers every
 * notification type and doesn't reset on navigating into bookings
 * specifically.
 *
 * The counting itself lives in useUnseenNotifications, which this shares with
 * the quote-requests badge -- including the reason it is not route-based (on
 * mobile, "Mis reservas" and "Reservas entrantes" share one URL).
 */
export const IncomingBookingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { unseenCount, setViewing } = useUnseenNotifications(WATCHED_TYPES, isAuthenticated);

  return (
    <IncomingBookingsContext.Provider
      value={{ unseenCount, setViewingIncomingBookings: setViewing }}
    >
      {children}
    </IncomingBookingsContext.Provider>
  );
};

export const useIncomingBookingsBadge = (): IncomingBookingsContextProps => {
  const context = useContext(IncomingBookingsContext);
  if (!context) throw new Error('useIncomingBookingsBadge must be used within an IncomingBookingsProvider');
  return context;
};
